import React, { Component } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import dayjs from '../../../../dayjs/dayjs_timezone';
import { SHORT_DATE_FORMAT } from '../../../constants';
import Select from '../../../components/Common/Select';
import { customSelectStyles, formatAvailabilityMessage } from '../../../utilities/helperUtility';
import { formatHours } from '../../../utilities/timeUtility';

export default class TimeSlotsSelect extends Component {
  parseTimeSlots() {
    const {
      availableDates,
      selectedDate,
      timeslots,
      timeZone,
    } = this.props;

    if (!timeslots.length || !selectedDate) return [];

    const day = dayjs.tz(selectedDate, timeZone).format(SHORT_DATE_FORMAT);
    if (!availableDates.includes(day)) return [];

    const blockTimeSlot = find(timeslots, { date: day });
    return blockTimeSlot ? this.sortedTimeslots(blockTimeSlot.timeslots) : [];
  }

  sortedTimeslots(timeslots) {
    const { isFinish } = this.props;
    return [...timeslots].sort((x, y) => {
      const date = dayjs(isFinish ? y.finish : y.start);
      return date.isSameOrAfter(dayjs(isFinish ? x.finish : x.start)) ? -1 : 1;
    });
  }

  parseSelectOptions(timeslots) {
    const {
      isFinish,
      isDisplayRange,
      isHideBookedOutSlots,
      isShowRemainingSpace,
      isShowWaitlist,
      locale,
      maxAvailability,
      maxDate,
      minDate,
      quantity,
      selectedTimeSlots = [],
      translation,
      timeFormat,
      timeRestriction,
      timeZone,
      variant,
    } = this.props;

    const now = dayjs().tz(timeZone);
    const parsedTimeslots = [];
    const unitMultiplier = variant?.unit_multiplier || 1;

    timeslots.map((x) => {
      const slotTime = isFinish ? x.finish : x.start;
      const slotAvailable = (isFinish ? x.finish_available : x.available) || 0;
      let available = maxAvailability && maxAvailability <= slotAvailable ? maxAvailability : slotAvailable;
      available = parseInt(available / unitMultiplier, 10);
      const minTime = minDate ? now.clone().add(minDate, 'seconds') : now.clone();
      const maxTime = maxDate ? now.clone().add(maxDate, 'days') : now.clone().add(2, 'years');
      const time = dayjs(slotTime).tz(timeZone);
      const restricted = timeRestriction ? time.isSameOrBefore(timeRestriction) : false;
      const isAlreadySelect = selectedTimeSlots.length && selectedTimeSlots.some(slot => isEqual(slot, x));

      if (isAlreadySelect) available = available - quantity;

      const isQuantityAvailable = available >= quantity && !isAlreadySelect;
      const isTimeAvailable = time.isSameOrAfter(minTime) && time.isSameOrBefore(maxTime);
      const isDisabled = restricted || !(isTimeAvailable && (isShowWaitlist || isQuantityAvailable));

      if (!isHideBookedOutSlots || !isDisabled) {
        let message = `${formatHours(slotTime, locale, timeZone, timeFormat)} `;

        if (isDisplayRange && !isFinish) message += `- ${formatHours(x.finish, locale, timeZone, timeFormat)} `;
        if (isShowRemainingSpace) message += `/ ${formatAvailabilityMessage(available, available, translation)} `;

        parsedTimeslots.push({
          label: message,
          value: slotTime,
          details: x.status_detail,
          isDisabled,
        });
      }
    });

    return parsedTimeslots;
  }

  shouldComponentUpdate(nextProps) {
    return Object.keys(this.props).some((key) => key !== 'onSelect' && !isEqual(this.props[key], nextProps[key]));
  }

  placeholder() {
    const { translation } = this.props;
    return translation.key('fieldDropdownPrompt') || 'Select...';
  }

  noOptionsMessage() {
    const { translation } = this.props;
    return () => translation.key('noOptionsMessage');
  }

  render() {
    const {
      customClassName,
      isFinish,
      style,
      loading,
      onMenuClose,
      onMenuOpen,
      onSelect,
      selectedTimeSlot,
    } = this.props;

    const timeslots = this.parseTimeSlots();
    const selectOptions = this.parseSelectOptions(timeslots);
    const selectedTime = isFinish ? selectedTimeSlot?.finish : selectedTimeSlot?.start;

    return (
      <div className={`pt8 pb10 timeslots-select ${customClassName}`}>
        <Select
          className="Select-dropup"
          disableDefaultOption
          isClearable={false}
          styles={customSelectStyles(style.select)}
          isSearchable={false}
          backspaceRemovesValue={false}
          value={selectedTime}
          options={selectOptions}
          onChange={onSelect}
          isLoading={loading}
          isDisabled={loading}
          placeholder={this.placeholder()}
          noOptionsMessage={this.noOptionsMessage()}
          buildLabel={true}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          menuPlacement="auto"
        />
      </div>
    );
  }
}

TimeSlotsSelect.propTypes = {
  timeslots: PropTypes.array,
  timeZone: PropTypes.string,
  locale: PropTypes.string,
  isDisplayRange: PropTypes.bool,
  isShowRemainingSpace: PropTypes.bool,
  availableDates: PropTypes.array,
  timeFormat: PropTypes.string,
  selectedDate: PropTypes.any,
  translation: PropTypes.object,
};
